import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Truck, RotateCcw, SlidersHorizontal } from "lucide-react";
import { fetchProduct } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const PRODUCT_IMG = "https://customer-assets.emergentagent.com/job_04829c98-cd55-43c7-9947-d6a9dd40b6ff/artifacts/yc6g90h6_IMG_5079.jpeg";

const createRipple = (e) => {
  const btn = e.currentTarget;
  const rect = btn.getBoundingClientRect();
  const span = document.createElement("span");
  const size = Math.max(rect.width, rect.height);
  span.style.width = span.style.height = size + "px";
  span.style.left = e.clientX - rect.left - size / 2 + "px";
  span.style.top = e.clientY - rect.top - size / 2 + "px";
  span.className = "ripple-effect";
  btn.appendChild(span);
  setTimeout(() => span.remove(), 700);
};

const BuySection = () => {
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem, loading } = useCart();

  useEffect(() => {
    fetchProduct().then(setProduct).catch(() => {});
  }, []);

  if (!product) {
    return (
      <section id="buy" className="py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-white/50 text-sm font-body" data-testid="buy-loading">
          Loading product…
        </div>
      </section>
    );
  }

  const handleAdd = (e) => {
    createRipple(e);
    addItem({ quantity: qty });
  };

  const total = (product.price * qty).toFixed(2);

  return (
    <section id="buy" data-testid="buy-section" className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="relative flex items-center justify-center"
        >
          <div className="absolute w-[400px] h-[400px] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(0,122,255,0.16) 0%, transparent 65%)", filter: "blur(30px)" }} />
          <div className="levitate relative">
            <img src={PRODUCT_IMG} alt="GripForce X" className="w-[360px] sm:w-[440px] product-mask" draggable={false} />
          </div>
          <div className="absolute bottom-4 right-4 text-[10px] uppercase tracking-[0.3em] text-white/40 font-body">
            01 / 01 · GFX-X
          </div>
        </motion.div>

        {/* Buy panel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="space-y-8"
        >
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#007AFF] mb-4 font-body">// 04 Acquire</div>
            <h2 className="font-heading text-5xl sm:text-6xl leading-[0.95]">{product.name.toUpperCase()}</h2>
            <p className="mt-4 text-white/55 text-sm max-w-md leading-relaxed">{product.tagline}</p>
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-4 border-y border-white/10 py-6">
            <span className="font-heading text-5xl text-[#007AFF]" data-testid="buy-price">${product.price}</span>
            <span className="text-xs uppercase tracking-[0.2em] text-white/40 font-body">USD · Free Shipping</span>
          </div>

          {/* Single adjustable unit callout */}
          <div className="flex items-start gap-4 border border-white/10 p-5" data-testid="adjustable-callout">
            <div className="w-10 h-10 flex items-center justify-center border border-[#007AFF]/40 text-[#007AFF] shrink-0">
              <SlidersHorizontal className="w-4 h-4" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-heading text-xl tracking-wide mb-1">FULLY ADJUSTABLE · 40–99 LB</div>
              <p className="text-xs text-white/55 font-body leading-relaxed">
                One sphere covers every tier. Dial the tension in seconds — no extra units, no tiers to choose.
              </p>
            </div>
          </div>

          {/* Quantity + CTA */}
          <div className="flex flex-col sm:flex-row items-stretch gap-4">
            <div className="flex items-center border border-white/15" data-testid="qty-selector">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-12 h-14 flex items-center justify-center hover:bg-white/5 transition"
                data-testid="qty-decrement"
                aria-label="Decrease quantity"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-12 text-center font-heading text-2xl" data-testid="qty-value">{qty}</span>
              <button
                onClick={() => setQty((q) => Math.min(99, q + 1))}
                className="w-12 h-14 flex items-center justify-center hover:bg-white/5 transition"
                data-testid="qty-increment"
                aria-label="Increase quantity"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={handleAdd}
              disabled={loading}
              data-testid="buy-add-to-cart-button"
              className="cta-glow relative overflow-hidden flex-1 inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#007AFF] text-black font-body font-bold text-sm uppercase tracking-[0.25em] hover:bg-white hover:text-[#0a0a0a] transition-colors disabled:opacity-60"
            >
              <ShoppingBag className="w-4 h-4" strokeWidth={2.5} />
              {loading ? "Adding…" : `Add to Cart — $${total}`}
            </button>
          </div>

          {/* Reassurance */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
            {[
              { Icon: Truck, label: "Free Shipping" },
              { Icon: RotateCcw, label: "30-Day Returns" },
            ].map(({ Icon, label }) => (
              <div key={label} className="flex flex-col items-start gap-2">
                <Icon className="w-4 h-4 text-[#007AFF]" strokeWidth={1.6} />
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-body">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BuySection;
